import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";

interface PaymentCard {
  id: string;
  brand: "visa" | "mastercard" | "amex";
  last4: string;
  expMonth: string;
  expYear: string;
  holder: string;
  type: "Crédito" | "Débito";
  status: "active" | "expired";
  isPrincipal: boolean;
  addedDate: string;
  lastUsed?: string;
}

export default function MetodosPagoSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const savedCards: PaymentCard[] = [
    {
      id: "1",
      brand: "visa",
      last4: "4242",
      expMonth: "12",
      expYear: "2026",
      holder: "María González",
      type: "Crédito",
      status: "active",
      isPrincipal: true,
      addedDate: "15 Ene 2024",
    },
    {
      id: "2",
      brand: "mastercard",
      last4: "8888",
      expMonth: "03",
      expYear: "2024",
      holder: "Carlos Ramírez",
      type: "Débito",
      status: "expired",
      isPrincipal: false,
      addedDate: "10 Nov 2023",
      lastUsed: "28 Feb 2024",
    },
    {
      id: "3",
      brand: "amex",
      last4: "1005",
      expMonth: "08",
      expYear: "2025",
      holder: "Laura Martínez",
      type: "Crédito",
      status: "active",
      isPrincipal: false,
      addedDate: "02 Mar 2024",
    },
  ];

  const getBrandIcon = (brand: string) => {
    switch (brand) {
      case "visa":
        return (
          <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center">
            <span className="text-white text-xs font-bold">VISA</span>
          </div>
        );
      case "mastercard":
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded flex items-center justify-center">
            <div className="flex">
              <div className="w-3 h-3 bg-red-600 rounded-full opacity-80"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full -ml-1 opacity-80"></div>
            </div>
          </div>
        );
      case "amex":
        return (
          <div className="w-12 h-8 bg-blue-500 rounded flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">AMEX</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full p-4 sm:p-6 overflow-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            Métodos de pago
          </h1>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary} mt-1`}>
            Administra tus métodos de pago para suscripciones y servicios.
          </p>
        </div>
        <button className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-xl transition-all font-medium whitespace-nowrap text-sm sm:text-base">
          <i className="ri-add-line"></i>
          <span>Agregar método</span>
        </button>
      </div>

      {/* Bank Transfer Section */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 mb-4 sm:mb-6`}>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-bank-line text-primary-500 text-2xl"></i>
          </div>
          <div className="flex-1 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <h2 className={`text-base sm:text-lg font-semibold ${themeClasses.text}`}>
                Pago por Transferencia
              </h2>
              <span className="text-xs font-medium text-primary-500 bg-primary-500/10 px-3 py-1 rounded-full w-fit">
                Disponible
              </span>
            </div>
            <p className={`text-sm ${themeClasses.textSecondary} mt-2`}>
              Realiza tu pago mediante transferencia bancaria utilizando los siguientes datos. Asegúrate de incluir tu número de referencia.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'}`}>
                <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Banco</p>
                <p className={`text-sm font-medium ${themeClasses.text}`}>BBVA México</p>
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'}`}>
                <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Beneficiario</p>
                <p className={`text-sm font-medium ${themeClasses.text}`}>Company Technologies S.A. de C.V.</p>
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'}`}>
                <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>CLABE Interbancaria</p>
                <div className="flex items-center space-x-2">
                  <p className={`text-sm font-medium ${themeClasses.text} font-mono`}>012 180 0001 2345 6789</p>
                  <button aria-label="Copiar" className="text-primary-500 hover:text-[var(--brand-primary-hover)]">
                    <i className="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'}`}>
                <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Cuenta</p>
                <div className="flex items-center space-x-2">
                  <p className={`text-sm font-medium ${themeClasses.text} font-mono`}>0123456789</p>
                  <button aria-label="Copiar" className="text-primary-500 hover:text-[var(--brand-primary-hover)]">
                    <i className="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Reference Number */}
            <div className={`mt-4 p-3 sm:p-4 rounded-xl border-2 border-dashed border-primary-500/50 ${isDarkMode ? 'bg-primary-500/5' : 'bg-primary-500/10'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>Tu número de referencia</p>
                  <p className={`text-base sm:text-xl font-bold font-mono ${themeClasses.text} mt-1 break-all`}>REF-DMX-2024-00847</p>
                </div>
                <button className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg transition-all font-medium text-sm">
                  <i className="ri-file-copy-line"></i>
                  <span>Copiar</span>
                </button>
              </div>
              <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                <i className="ri-information-line mr-1"></i>
                Incluye este número en el concepto de tu transferencia para identificar tu pago automáticamente.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {savedCards.map((card) => (
          <div
            key={card.id}
            className={`${themeClasses.cardBg} border ${
              card.status === "expired" ? "border-red-300 dark:border-red-500/50" : themeClasses.cardBorder
            } rounded-xl p-4 sm:p-5 relative`}
          >
            {/* Status Badge */}
            <div className="absolute top-4 right-4">
              {card.isPrincipal && (
                <span className="text-xs font-medium text-primary-500 bg-primary-500/10 px-3 py-1 rounded-full">
                  Principal
                </span>
              )}
              {card.status === "expired" && (
                <span className="text-xs font-medium text-red-500 bg-red-500/10 px-3 py-1 rounded-full">
                  Expirada
                </span>
              )}
            </div>

            {/* Card Header */}
            <div className="flex items-center space-x-3 mb-4">
              {getBrandIcon(card.brand)}
              <div>
                <p className={`font-medium ${themeClasses.text}`}>
                  {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4}
                </p>
                <p className={`text-xs ${card.status === "expired" ? "text-red-500" : themeClasses.textSecondary}`}>
                  Expira {card.expMonth}/{card.expYear}
                </p>
              </div>
            </div>

            {/* Expired Warning */}
            {card.status === "expired" && (
              <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <i className="ri-error-warning-fill text-red-500 mt-0.5"></i>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Tarjeta expirada. Esta tarjeta ya no puede procesarse. Actualízala o agrega un nuevo método de pago para evitar interrupciones en el servicio.
                  </p>
                </div>
              </div>
            )}

            {/* Card Details */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Titular</p>
                <p className={`text-sm font-medium ${themeClasses.text}`}>{card.holder}</p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Tipo</p>
                <p className={`text-sm font-medium ${themeClasses.text}`}>{card.type}</p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Estado</p>
                <p className={`text-sm font-medium ${card.status === "active" ? "text-primary-500" : "text-red-500"}`}>
                  {card.status === "active" ? "Activa" : "Expirada"}
                </p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>{card.lastUsed ? "Último uso" : "Agregada"}</p>
                <p className={`text-sm font-medium ${themeClasses.text}`}>{card.lastUsed || card.addedDate}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button className={`flex-1 px-4 py-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-all text-sm font-medium`}>
                {card.status === "expired" ? "Eliminar" : "Editar"}
              </button>
              <button className={`flex-1 px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                card.status === "expired" 
                  ? "bg-red-500 hover:bg-red-600 text-white" 
                  : "bg-primary-500 hover:bg-primary-400 text-gray-900"
              }`}>
                {card.status === "expired" ? "Actualizar tarjeta" : card.isPrincipal ? "Gestionar" : "Establecer principal"}
              </button>
            </div>

            {/* Security Badge */}
            {card.status === "active" && (
              <div className="flex items-center space-x-1 mt-3">
                <i className={`ri-shield-check-line text-xs ${themeClasses.textSecondary}`}></i>
                <span className={`text-xs ${themeClasses.textSecondary}`}>
                  {card.isPrincipal ? "Protegido con cifrado SSL de 256 bits" : "Verificada y protegida"}
                </span>
              </div>
            )}
          </div>
        ))}

        {/* Add New Method Card */}
        <div className={`${themeClasses.cardBg} border-2 border-dashed ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center min-h-[200px] sm:min-h-[280px]`}>
          <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <i className={`ri-bank-card-line text-2xl ${themeClasses.textSecondary}`}></i>
          </div>
          <p className={`text-base font-medium ${themeClasses.text} text-center`}>
            Agregar nuevo método
          </p>
          <p className={`text-sm ${themeClasses.textSecondary} text-center mt-1 max-w-[200px]`}>
            Agrega una tarjeta adicional para tener respaldo en caso de emergencia
          </p>
          <button className="flex items-center space-x-2 px-5 py-2.5 mt-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-xl transition-all font-medium">
            <i className="ri-add-line"></i>
            <span>Agregar tarjeta</span>
          </button>
        </div>
      </div>

      {/* Security Banner */}
      <div className={`${isDarkMode ? 'bg-dark-surface' : 'bg-gray-100'} rounded-xl p-5`}>
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-red-500 text-xl"></i>
          </div>
          <div>
            <p className={`font-semibold ${themeClasses.text}`}>Tus pagos están protegidos</p>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              La plataforma utiliza Stripe para procesar todos los pagos de forma segura. No almacenamos información completa de tarjetas en nuestros servidores.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="flex items-center space-x-1">
                <i className="ri-checkbox-circle-fill text-primary-500 text-sm"></i>
                <span className={`text-xs ${themeClasses.textSecondary}`}>Cifrado SSL 256-bit</span>
              </div>
              <div className="flex items-center space-x-1">
                <i className="ri-checkbox-circle-fill text-primary-500 text-sm"></i>
                <span className={`text-xs ${themeClasses.textSecondary}`}>Certificación PCI DSS Nivel 1</span>
              </div>
              <div className="flex items-center space-x-1">
                <i className="ri-checkbox-circle-fill text-primary-500 text-sm"></i>
                <span className={`text-xs ${themeClasses.textSecondary}`}>Autenticación 3D Secure</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
